import { useEffect, useRef, useState } from "react";

import Flicking, { ERROR_CODE, FlickingError } from "@egjs/react-flicking";
import { useHistory, useLocation } from "react-router-dom";

import Box from "@/components/Box";
import Grid from "@/components/Grid";
import ProductFilters from "@/components/ProductFilters";
import { ProductFiltersMobile } from "@/components/ProductFiltersSidebar";
import {
  dashboardMainLayoutPb,
  dashboardMainLayoutPt,
  dashboardMainLayoutPx,
  dashboardPageMaxWidth
} from "@/components/organisations/learningHub/dashboardLayout";
import { EXPERTS_OVERVIEW_URL, EXPERTS_SUPPORTED_TESTS_URL, EXPERTS_URL } from "@/core/urls";
import useWindowSize from "@/hooks/use-window-size";
import Center from "@/tpo/Center";
import Group from "@/tpo/Group";
import InfiniteScroll, { InfiniteScrollContext } from "@/tpo/InfiniteScroll";
import SegmentedControl from "@/tpo/SegmentedControl";
import SortMenu from "@/tpo/SortMenu";
import Stack from "@/tpo/Stack";
import { Search } from "@/tpo/TextInput";
import { useTheme } from "styled-components";

import ExpertsPageFiltersLayout from "./ExpertsPageFiltersLayout";

/** Auto-fit card columns; each card is at least 310px wide. */
export const LIST_CARD_GRID_TEMPLATE_COLUMNS = "repeat(auto-fill, minmax(310px, 1fr))";

/** Practitioners / supported tests list area — mobile padding 68, 16, 80, 16; tablet+ matches CMP shop. */
const expertsListLayoutPt = [68, dashboardMainLayoutPt[1], dashboardMainLayoutPt[2]];
const expertsListLayoutPb = [80, dashboardMainLayoutPb[1], dashboardMainLayoutPb[2]];
const expertsListLayoutPx = [16, dashboardMainLayoutPx[1], dashboardMainLayoutPx[2]];

function ListTemplateContainer({ children }) {
  return (
    <Center
      pt={expertsListLayoutPt}
      pb={expertsListLayoutPb}
      bg="haze"
      px={expertsListLayoutPx}
      alignItems="flex-start"
    >
      <Box width="100%" maxWidth={dashboardPageMaxWidth} mx="auto">
        {children}
      </Box>
    </Center>
  );
}

function ListTemplateHeader({ header, subheader }) {
  return (
    <Center stacked gap={28} mx="auto" pt={[40, 40, 80]} pb={60}>
      <Box fontFamily="gilroyBold" fontSize={[40, 40, 68]} maxWidth={1024} textAlign="center">
        {header}
      </Box>
      <Box fontFamily="gilroyRegular" fontSize={[24, 24, 32]} maxWidth={720} textAlign="center">
        {subheader}
      </Box>
    </Center>
  );
}

const tabs = [
  {
    value: EXPERTS_OVERVIEW_URL
  },
  {
    value: EXPERTS_URL
  },
  {
    value: EXPERTS_SUPPORTED_TESTS_URL
  }
];

function ListTemplateSwitcher({ children }) {
  const history = useHistory();
  const location = useLocation();
  const flickingApiRef = useRef();

  const { width } = useWindowSize();

  const align = width < 450 ? "0" : "center";

  return (
    <Box pt={[85, 85, 100]}>
      <Box bg="white" pt={[60, 60, 80]}>
        <Flicking
          align={align}
          key={width}
          bound
          moveType="freeScroll"
          ref={flickingApiRef}
          style={{
            paddingLeft: 20,
            paddingRight: 20
          }}
        >
          <SegmentedControl
            data={[
              {
                label: "overview",
                value: EXPERTS_OVERVIEW_URL
              },
              {
                label: "practitioners",
                value: EXPERTS_URL
              },
              {
                label: "supported tests",
                value: EXPERTS_SUPPORTED_TESTS_URL
              }
            ]}
            onChange={v => history.push(v)}
            value={location.pathname}
            size={["xs", "xs", "sm"]}
            bg="haze"
          />
        </Flicking>
      </Box>
      {children}
    </Box>
  );
}

function ListTemplateFilters({
  search,
  allFilters,
  validatedFilters,
  toggleFilter,
  controls,
  selectedFilters,
  filtersTitle,
  children
}) {
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();

  return (
    <Stack gap={[20]}>
      <Box
        display="flex"
        flexDirection={["column", "column", "row"]}
        gap={20}
        justifyContent={[null, null, "space-between"]}
        flexWrap="wrap"
      >
        <Group gap={10}>
          <ProductFilters
            allFilters={allFilters}
            validatedFilters={validatedFilters}
            drawerBg="haze"
            borderColor="dark"
            color={theme.colors.dark}
            filtersTitle={filtersTitle}
            toggleFilter={toggleFilter}
          />
          {children}
        </Group>
        <Box display="flex" flexDirection={["column", "column", "row"]} gap={10}>
          <Search
            placeholder="Search"
            value={search}
            onChange={s => {
              const searchParams = new URLSearchParams(location.search);
              searchParams.set("search", s);
              history.push({
                path: location.path,
                search: searchParams.toString()
              });
            }}
            maxWidth={[null, null, 350]}
            minWidth={310}
            width="100%"
          />
          <Box display="flex" justifyContent="flex-end">
            <SortMenu
              value={controls.sort}
              setValue={controls.setSort}
              open={controls.sortMenuOpen}
              setOpen={controls.setSortMenuOpen}
              options={controls.sortOptions}
              placeholder="Sort By"
            />
          </Box>
        </Box>
      </Box>
      {selectedFilters}
    </Stack>
  );
}

ListTemplateFilters.defaultProps = {
  filtersTitle: "Filters"
};

function ListTemplateFilteredContent({
  filterSidebar,
  search,
  allFilters,
  validatedFilters,
  toggleFilter,
  resetFilters,
  controls,
  filtersTitle,
  contentAfterTop,
  getFilterLabel,
  toolbarFooter = null,
  children
}) {
  const location = useLocation();
  const history = useHistory();

  const toolbar = (
    <Stack gap={20} minWidth={0} width="100%">
      <Group
        gap={8}
        alignItems="center"
        justifyContent={["flex-start", "flex-end", "flex-end"]}
        flexWrap="wrap"
        width="100%"
      >
        <ProductFiltersMobile
          allFilters={allFilters}
          validatedFilters={validatedFilters}
          toggleFilter={toggleFilter}
          onReset={resetFilters}
          filtersTitle={filtersTitle}
          contentAfterTop={contentAfterTop}
          getFilterLabel={getFilterLabel}
        />
        <Search
          placeholder="Search"
          value={search}
          onChange={s => {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("search", s);
            history.push({
              path: location.path,
              search: searchParams.toString()
            });
          }}
          maxWidth={[null, null, 350]}
          minWidth={302}
          flex="0 1 auto"
        />
        <SortMenu
          value={controls.sort}
          setValue={controls.setSort}
          open={controls.sortMenuOpen}
          setOpen={controls.setSortMenuOpen}
          options={controls.sortOptions}
          placeholder="Sort By"
        />
      </Group>
      {toolbarFooter}
    </Stack>
  );

  return (
    <ExpertsPageFiltersLayout filterSidebar={filterSidebar} toolbar={toolbar}>
      <Stack gap={[20, 20, 40]} minWidth={0} width="100%">
        {children}
      </Stack>
    </ExpertsPageFiltersLayout>
  );
}

ListTemplateFilteredContent.defaultProps = {
  filtersTitle: "Filters"
};

function ListTemplateGrid({
  fetchMore,
  hasMore,
  endCursor,
  loading,
  items,
  ItemCard,
  gridTemplateColumns = LIST_CARD_GRID_TEMPLATE_COLUMNS
}) {
  return (
    <Grid gridTemplateColumns={gridTemplateColumns} gridColumnGap={16} gridRowGap={16}>
      <InfiniteScroll
        hasMore={hasMore}
        next={() =>
          fetchMore({
            variables: {
              after: endCursor,
              first: 10
            }
          })
        }
        loading={loading}
      >
        <InfiniteScrollContext.Consumer>
          {({ setBottomElement }) => (
            <>
              {items?.slice(0, -1)?.map(expert => (
                <ItemCard key={expert.id} {...expert} />
              ))}
              {items?.slice(-1)?.map(expert => (
                <ItemCard key={expert.id} ref={setBottomElement} {...expert} />
              ))}
            </>
          )}
        </InfiniteScrollContext.Consumer>
      </InfiniteScroll>
    </Grid>
  );
}

export default Object.assign({
  Container: ListTemplateContainer,
  Switcher: ListTemplateSwitcher,
  Header: ListTemplateHeader,
  Filters: ListTemplateFilters,
  FilteredContent: ListTemplateFilteredContent,
  Grid: ListTemplateGrid
});
